/**
 * JobDetailPage — /jobs/:jobId  (jobId is either uuid or slug)
 * Public job detail + inline Apply form for signed-in applicants.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { labelFor, findGroupForSub } from "@/data/jobCategories";
import {
  ArrowLeft, MapPin, Briefcase, Building2, User, Wifi, Loader2, Send,
  Bookmark, BookmarkCheck, Calendar, Mail, Phone, ShieldCheck, LayoutDashboard,
} from "lucide-react";
import { ReportButton } from "@/pages/JobsDashboardPage";

export default function JobDetailPage() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [saved, setSaved] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [apply, setApply] = useState({ cover_note: "", resume_url: "", portfolio_links: [""], references: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/jobs/${jobId}`);
      setJob(data);
      if (user) {
        // Am I already an applicant?
        try {
          const { data: mine } = await http.get("/me/applications");
          setApplied((mine.items || []).some((a) => a.job_id === data.id));
        } catch (_) { /* noop */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Job not found.");
      nav("/jobs");
    } finally { setLoading(false); }
  }, [jobId, user, nav]);

  useEffect(() => { load(); }, [load]);

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save jobs."); return; }
    try {
      const { data } = await http.post(`/jobs/${job.id}/save`);
      setSaved(data.saved);
      toast.success(data.saved ? "Job saved." : "Removed from saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const submitApply = async () => {
    setApplying(true);
    try {
      const payload = {
        ...apply,
        portfolio_links: apply.portfolio_links.filter((s) => s.trim()),
      };
      await http.post(`/jobs/${job.id}/apply`, payload);
      toast.success("Application sent!");
      setApplied(true); setShowApply(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed."); }
    finally { setApplying(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>;
  if (!job) return null;

  const g = findGroupForSub(job.category);
  const isOwner = user && (job.posted_by === user.id);
  const canApply = user && !isOwner && !applied && job.status === "active";

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="job-detail-page">
      <Link to="/jobs" className="text-xs opacity-70 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All jobs
      </Link>

      <div className="wofy-card !p-6 mb-4">
        {job.cover_image && (
          <img src={job.cover_image} alt="" className="w-full h-48 object-cover rounded-lg mb-4" />
        )}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-lg">{g?.emoji}</span>
          <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{g?.label}</span>
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{labelFor(job.employment_type)}</span>
          {job.remote && <span className="text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full" style={{ color: "var(--wofy-primary)" }}><Wifi className="w-2.5 h-2.5" /> Remote</span>}
          {job.poster_type === "community" && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "var(--wofy-secondary)", color: "white" }}><ShieldCheck className="w-2.5 h-2.5" /> Community</span>}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-2" data-testid="job-title">{job.title}</h1>
        <div className="text-sm opacity-90 mb-3 flex flex-wrap items-center gap-3">
          {job.business?.name ? (
            <Link to={`/business/${job.business.slug}`} className="inline-flex items-center gap-1 hover:underline">
              <Building2 className="w-3.5 h-3.5" /> {job.business.name}
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {job.posted_by_name || "Individual"}</span>
          )}
          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.remote ? "Remote" : [job.city, job.state, job.country].filter(Boolean).join(", ") || "—"}</span>
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Posted {new Date(job.published_at || job.created_at).toLocaleDateString()}</span>
        </div>
        {job.salary && <div className="text-sm font-semibold mb-3" style={{ color: "var(--wofy-primary)" }}>💰 {job.salary}</div>}

        <div className="flex flex-wrap gap-2 mt-4">
          {canApply && (
            <button onClick={() => setShowApply(true)} className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="job-apply-btn">
              <Send className="w-4 h-4" /> Apply now
            </button>
          )}
          {applied && (
            <span className="text-sm text-emerald-700 inline-flex items-center gap-1.5" data-testid="job-already-applied">
              <ShieldCheck className="w-4 h-4" /> You applied to this job
            </span>
          )}
          {user && (
            <button onClick={toggleSave} className="wofy-btn-ghost inline-flex items-center gap-1.5" data-testid="job-save-btn">
              {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              {saved ? "Saved" : "Save"}
            </button>
          )}
          {isOwner && (
            <>
              <Link to="/jobs/dashboard" className="wofy-btn-ghost inline-flex items-center gap-1.5" data-testid="job-owner-dashboard">
                <LayoutDashboard className="w-4 h-4" /> Manage applications
              </Link>
              <span className="text-xs opacity-60 self-center">You posted this · {job.application_count || 0} application{job.application_count === 1 ? "" : "s"}</span>
            </>
          )}
          {user && !isOwner && <ReportButton kind="job" targetId={job.id} label="Report job" />}
        </div>
      </div>

      <Section title="Description" body={job.description} testid="job-description" />
      <Section title="Requirements" body={job.requirements} testid="job-requirements" />
      <Section title="Benefits"     body={job.benefits}     testid="job-benefits" />

      {job.contact_via !== "socialpulse" && job.contact_value && (
        <div className="wofy-card !p-4 mb-4" data-testid="job-contact">
          <div className="overline mb-1">CONTACT</div>
          <p className="text-sm inline-flex items-center gap-1.5">
            {job.contact_via === "email"
              ? <><Mail className="w-4 h-4" /> <a href={`mailto:${job.contact_value}`} className="underline">{job.contact_value}</a></>
              : <><Phone className="w-4 h-4" /> <a href={`tel:${job.contact_value}`} className="underline">{job.contact_value}</a></>}
          </p>
        </div>
      )}

      {showApply && (
        <div className="wofy-card !p-6 mb-6" data-testid="job-apply-form">
          <div className="overline mb-2">APPLICATION</div>
          <h3 className="font-display text-xl mb-3">Apply for {job.title}</h3>
          <label className="block mb-3">
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Cover note (optional)</div>
            <textarea rows={5} value={apply.cover_note} onChange={(e) => setApply({ ...apply, cover_note: e.target.value })} className="wofy-input" placeholder="Why are you a great fit for this role?" data-testid="apply-cover-note" />
          </label>
          <label className="block mb-3">
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Resume URL (optional)</div>
            <input value={apply.resume_url} onChange={(e) => setApply({ ...apply, resume_url: e.target.value })} className="wofy-input" placeholder="https://…" data-testid="apply-resume-url" />
          </label>
          <label className="block mb-3">
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Portfolio link (optional)</div>
            <input value={apply.portfolio_links[0]} onChange={(e) => setApply({ ...apply, portfolio_links: [e.target.value] })} className="wofy-input" placeholder="https://…" data-testid="apply-portfolio" />
          </label>
          <label className="block mb-4">
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">References (optional)</div>
            <textarea rows={2} value={apply.references} onChange={(e) => setApply({ ...apply, references: e.target.value })} className="wofy-input" data-testid="apply-references" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowApply(false)} className="wofy-btn-ghost">Cancel</button>
            <button onClick={submitApply} disabled={applying} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="apply-submit">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send application
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, body, testid }) {
  if (!body) return null;
  return (
    <div className="wofy-card !p-4 mb-4" data-testid={testid}>
      <div className="overline mb-2">{title.toUpperCase()}</div>
      <p className="text-sm whitespace-pre-line leading-relaxed">{body}</p>
    </div>
  );
}
